import { useMemo, useState } from 'react';
import { Table, Input, Dropdown, Button, Space, Typography } from 'antd';
import { Link, useLocation } from 'react-router-dom';
import {
    MoreOutlined,
    FileExcelOutlined,
    PhoneOutlined,
    CheckCircleFilled,
    CloseCircleFilled,
    ExclamationCircleFilled
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { CSVLink } from 'react-csv';
import { CONSTANT, Server } from '../../Utils';

const { Link: TextLink } = Typography;

const MICRO_ITEMS = [{ key: 'removeMicro', label: 'Remove Microbiology Encounter' }];
const ADMIN_ITEMS = [
    { key: 'deleteVisit', label: 'Delete Visit' },
    { key: 'deletePatient', label: 'Delete Patient' },
];

const csvHeadersBase = [
    { label: 'Patient Id', key: 'patient_identifier' },
    { label: 'Name', key: 'name' },
    { label: 'Gender', key: 'gender' },
    { label: 'Visit Date', key: 'created_at' },
    { label: 'Location', key: 'location' },
    { label: 'Provider', key: 'provider' },
];

export const TableComponent = ({ data = [], search = false, color = 'black', name = '', rowPerPage = 10, coordinator, onClickHandlerParent, personType, followUp = false, queried = false, onStatusClick }) => {
    const location = useLocation();
    const [nameValue, setNameValue] = useState('');
    const [patientIdValue, setPatientIdValue] = useState('');

    // Sort default: paper-form by created_at desc; follow-up by upcoming date asc;
    // queried (needs update) by when the query was raised desc, falling back to the
    // visit date for rows from an API that does not send encounter_updated_at
    const queriedAt = (row) => dayjs(row.encounter_updated_at || row.created_at).valueOf();
    const sortedData = useMemo(() => {
        if (!Array.isArray(data)) return [];
        return [...data].sort((a, b) => {
            if (followUp) {
                return (
                    (dayjs(a.upcoming_visit_date).valueOf() - dayjs(b.upcoming_visit_date).valueOf()) ||
                    String(a.patient_identifier || '').localeCompare(String(b.patient_identifier || ''))
                );
            }
            if (queried) {
                return (
                    (queriedAt(b) - queriedAt(a)) ||
                    (dayjs(b.created_at).valueOf() - dayjs(a.created_at).valueOf())
                );
            }
            return dayjs(b.created_at).valueOf() - dayjs(a.created_at).valueOf();
        });
    }, [data, followUp, queried]);

    const dataSource = useMemo(() => {
        let result = sortedData;
        if (patientIdValue) {
            result = result.filter(entry =>
                String(entry.patient_identifier || '').toLowerCase().includes(patientIdValue.toLowerCase())
            );
        }
        if (nameValue) {
            result = result.filter(entry =>
                String(entry.name || '').toLowerCase().includes(nameValue.toLowerCase())
            );
        }
        return result;
    }, [sortedData, patientIdValue, nameValue]);

    const onMenuClick = (key, record) => {
        if (!onClickHandlerParent) return;
        if (key === 'deleteVisit') onClickHandlerParent('delete visit', record);
        else if (key === 'deletePatient') onClickHandlerParent('delete patient', record);
        else if (key === 'removeMicro') onClickHandlerParent('delete encounter', record);
    };

    const FilterByPatientInput = (
        <Input
            placeholder="Search Patient Id"
            value={patientIdValue}
            onChange={e => setPatientIdValue(e.target.value || '')}
        />
    );

    const FilterByNameInput = (
        <Input
            placeholder="Search Name"
            value={nameValue}
            onChange={e => setNameValue(e.target.value || '')}
        />
    );

    const filterByKey = (arr, key) =>
        [...new Map(arr.map(item => [item[key], item])).values()]
            .filter(x => x[key])
            .map(da => ({ text: da[key], value: da[key] }));

    const locationFilters = useMemo(() => filterByKey(data, 'location'), [data]);
    const providerFilters = useMemo(() => filterByKey(data, 'provider'), [data]);

    const statusIcon = (status) => {
        const s = String(status || '').toLowerCase();
        if (s.includes('confirmed')) return <PhoneOutlined style={{ color: '#16a34a' }} />;
        if (s.includes('declined')) return <PhoneOutlined style={{ color: '#e11d48' }} />;
        if (s.includes('no response')) return <PhoneOutlined style={{ color: '#f59e0b' }} />;
        return <PhoneOutlined style={{ color: '#9ca3af' }} />;
    };

    // Base columns used in both modes
    const columns = [
        {
            title: '',
            dataIndex: '',
            key: 'color',
            render: () => <div style={{ width: 5, height: 50, background: color }} />
        },
        {
            title: !search ? FilterByPatientInput : 'Patient Id',
            dataIndex: 'patient_identifier',
            key: 'patient_identifier',
            render: (text, record) => (
                <Link to={`/visitsummary/${record.key}`} state={{ ...location.state, data }}>
                    {text}
                </Link>
            )
        },
        {
            title: !search ? FilterByNameInput : 'Name',
            dataIndex: 'name',
            key: 'name',
            width: 250
        }
    ];

    // Paper-form specific columns
    if (!followUp) {
        columns.push(
            { title: 'Gender', dataIndex: 'gender', key: 'gender' },
            {
                title: 'Visit Date',
                dataIndex: 'created_at',
                key: 'created_at',
                sorter: (a, b) => dayjs(a.created_at).valueOf() - dayjs(b.created_at).valueOf(),
                render: (t) => dayjs(t).format('M/D/YYYY')
            }
        );
        if (queried) {
            columns.push({
                title: 'Queried on',
                dataIndex: 'encounter_updated_at',
                key: 'encounter_updated_at',
                sorter: (a, b) => queriedAt(a) - queriedAt(b),
                render: (t) => (t ? dayjs(t).format('M/D/YYYY') : '-')
            });
        }
    }

    // Shared columns
    columns.push({
        title: 'Location',
        key: 'location',
        dataIndex: 'location',
        filters: locationFilters,
        onFilter: (value, record) => String(record.location || '').includes(value)
    });

    // Follow-up specific columns
    if (followUp) {
        columns.push(
            {
                title: 'Upcoming visit date',
                dataIndex: 'upcoming_visit_date',
                key: 'upcoming_visit_date',
                sorter: (a, b) => dayjs(a.upcoming_visit_date).valueOf() - dayjs(b.upcoming_visit_date).valueOf(),
                render: (t) => (t ? dayjs(t).format('M/D/YYYY') : '-')
            },
            {
                title: 'Last visit date',
                dataIndex: 'last_visit_date',
                key: 'last_visit_date',
                sorter: (a, b) => dayjs(a.last_visit_date).valueOf() - dayjs(b.last_visit_date).valueOf(),
                render: (t) => (t ? dayjs(t).format('M/D/YYYY') : '-')
            },
            {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
                filters: [
                    { text: 'Confirmed', value: 'Confirmed' },
                    { text: 'No response', value: 'No response' },
                    { text: 'Declined', value: 'Declined' },
                    { text: 'Not contacted', value: 'Not contacted' },
                ],
                onFilter: (value, record) => record.status === value,
                render: (text, record) => (
                    <span style={{ cursor: 'pointer', color: '#1677ff' }} onClick={() => onStatusClick('openModal', record)}>
                        {statusIcon(text)} &nbsp;
                        {text || 'Not contacted'}
                    </span>
                ),
            },
            {
                title: 'Status Date',
                dataIndex: 'status_date',
                key: 'status_date',
                sorter: (a, b) => dayjs(a.status_date).valueOf() - dayjs(b.status_date).valueOf(),
                render: (t) => (t ? dayjs(t).format('M/D/YYYY') : '-')
            }
        );
    }

    // Provider column (paper-form only; hide in follow-up mode and when search=true)
    if (!search && !followUp) {
        columns.push({
            title: 'Provider',
            key: 'provider',
            dataIndex: 'provider',
            filters: providerFilters,
            onFilter: (value, record) => String(record.provider || '').includes(value)
        });
    }

    // Admin/Coordinator Actions
    const isAdmin = [CONSTANT.SYSTEMADMIN, CONSTANT.REVIEWER].includes(personType);
    if (coordinator) {
        const items = isAdmin ? [...ADMIN_ITEMS, ...MICRO_ITEMS] : MICRO_ITEMS;
        columns.push({
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <Dropdown
                    menu={{ items, onClick: ({ key }) => onMenuClick(key, record) }}
                    trigger={['click']}
                >
                    <Button type="text" aria-label="Row actions" icon={<MoreOutlined />} />
                </Dropdown>
            )
        });
    } else if (isAdmin) {
        columns.push({
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <Dropdown
                    menu={{ items: ADMIN_ITEMS, onClick: ({ key }) => onMenuClick(key, record) }}
                    trigger={['click']}
                >
                    <Button type="text" aria-label="Row actions" icon={<MoreOutlined />} />
                </Dropdown>
            )
        });
    }

    // CSV headers adapt to follow-up mode
    const csvHeaders = followUp
        ? [
            { label: 'Patient Id', key: 'patient_identifier' },
            { label: 'Name', key: 'name' },
            { label: 'Gender', key: 'gender' },
            { label: 'Upcoming visit date', key: 'upcoming_visit_date' },
            { label: 'Last visit date', key: 'last_visit_date' },
            { label: 'Location', key: 'location' },
            { label: 'Status', key: 'status' },
            { label: 'Status Date', key: 'status_date' },
            { label: 'Provider', key: 'provider' }, // included in CSV even if hidden visually
        ]
        : csvHeadersBase;

    return (
        <>
            <div style={{ textAlign: 'right', marginBottom: 5 }}>
                <CSVLink
                    headers={csvHeaders}
                    filename={`${Server.ENV.name}_${name}.csv`}
                    data={dataSource}
                >
                    <FileExcelOutlined style={{ cursor: 'pointer', width: 20 }} />
                </CSVLink>
            </div>
            <Table
                rowKey={(r) => r.key ?? r.id}
                columns={columns}
                dataSource={dataSource}
                pagination={{ pageSize: rowPerPage }}
            />
        </>
    );
};
